import { ref } from 'vue'

import { defineStore } from 'pinia'
import type { IGroup, IGroupMember, IMembershipQuestions } from '../types/types'

/**
 * Groups UI Store - Handles UI-only state for groups
 * All data fetching is handled by the query store
 */
export const useGroupsUIStore = defineStore('groups-ui', () => {
  // ===== UI STATE =====
  const currentGroup = ref<IGroup | null>(null)
  const isCurrentGroupLoading = ref(false)
  const groupOwner = ref<IGroupMember | null>(null)
  const showMembershipQuestions = ref(false)
  const membershipQuestionsMode = ref<'answer' | 'view'>('answer')
  const isInitialized = ref(false)
  const isGroupInfoHidden = ref(false)
  const currentGroupMembershipQuestions = ref<IMembershipQuestions | null>(null)
  const showJoinGroupModal = ref(false)
  const showJoinSuccessConfetti = ref(false)
  /**
   * Order id captured from a successful paid group payment, kept here so it
   * survives the questionnaire detour. Mirrors the legacy ghl-revex-frontend
   * `GroupState.state.orderId` stash so the eventual `joinGroup` call (fired
   * from the membership-questions modal) can include the order context.
   */
  const pendingJoinOrderId = ref<string | null>(null)

  // ===== ACTIONS =====
  const setCurrentGroup = (group: IGroup | null) => {
    currentGroup.value = group
  }

  const setGroupOwner = (owner: IGroupMember | null) => {
    groupOwner.value = owner
  }

  const setCurrentGroupMembershipQuestions = (questions: IMembershipQuestions | null) => {
    currentGroupMembershipQuestions.value = questions
  }

  const setShowMembershipQuestions = (value: boolean) => {
    showMembershipQuestions.value = value
  }

  const setMembershipQuestionsMode = (mode: 'answer' | 'view') => {
    membershipQuestionsMode.value = mode
  }

  const setInitialized = (value: boolean) => {
    isInitialized.value = value
  }
  const setIsCurrentGroupLoading = (value: boolean) => {
    isCurrentGroupLoading.value = value
  }

  const toggleGroupInfo = () => {
    isGroupInfoHidden.value = !isGroupInfoHidden.value
  }

  const setGroupInfoHidden = (value: boolean) => {
    isGroupInfoHidden.value = value
  }

  const setShowJoinGroupModal = (value: boolean) => {
    showJoinGroupModal.value = value
  }

  const setShowJoinSuccessConfetti = (value: boolean) => {
    showJoinSuccessConfetti.value = value
  }

  const setPendingJoinOrderId = (value: string | null) => {
    pendingJoinOrderId.value = value
  }

  const reset = () => {
    currentGroup.value = null
    groupOwner.value = null
    showMembershipQuestions.value = false
    membershipQuestionsMode.value = 'answer'
    isInitialized.value = false
    isGroupInfoHidden.value = false
    showJoinGroupModal.value = false
    showJoinSuccessConfetti.value = false
    pendingJoinOrderId.value = null
  }

  // ===== PUBLIC API =====
  return {
    // State
    currentGroup,
    isCurrentGroupLoading,
    groupOwner,
    showMembershipQuestions,
    membershipQuestionsMode,
    isInitialized,
    isGroupInfoHidden,
    currentGroupMembershipQuestions,
    showJoinGroupModal,
    showJoinSuccessConfetti,
    pendingJoinOrderId,

    // Actions
    setCurrentGroup,
    setGroupOwner,
    setShowMembershipQuestions,
    setMembershipQuestionsMode,
    setInitialized,
    reset,
    setIsCurrentGroupLoading,
    toggleGroupInfo,
    setGroupInfoHidden,
    setCurrentGroupMembershipQuestions,
    setShowJoinGroupModal,
    setShowJoinSuccessConfetti,
    setPendingJoinOrderId,
  }
})
